import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  CheckCircle,
  Award,
  ChevronDown,
  ChevronUp,
  Edit3,
  Eye
} from 'lucide-react';
import QuestionHtmlWithZoom from './QuestionHtmlWithZoom';
import QuillEditorWithImageResize from './QuillEditorWithImageResize';
import SubjectiveTextAnswerArea from './SubjectiveTextAnswerArea';
import ScratchResultDetail from './ScratchResultDetail';
import PythonCodingResultDetail from './PythonCodingResultDetail';
import CppCodingResultDetail from './CppCodingResultDetail';
import SubjectiveTextResultDetail from './SubjectiveTextResultDetail';

interface Question {
  id: string;
  type: string;
  questionTitle?: string; // 题目名称（非客观题专用，管理用）
  questionStem: string; // 题干内容（富文本，包含解析的试卷题干）
  title?: string; // 保留用于向后兼容
  score: number;
  content?: string; // 题干HTML
  referenceAnswer?: string; // 参考答案HTML
  scoringPoints?: string; // 评分规则HTML
  userAnswer?: string;
}

type DisplayMode = 'answer' | 'preview' | 'detail' | 'edit';

interface SubjectiveQuestionCardProps {
  question: Question;
  paperId?: string;
  mode: DisplayMode;
  answer?: string;
  teacherFeedback?: { score?: number; feedback?: string };
  aiExplanation?: string;
  onTextAnswerChange?: (questionId: string, text: string) => void;
  onAIGrading?: (question: Question) => void;
  // 编辑模式回调
  onContentChange?: (content: string) => void;
  onReferenceAnswerChange?: (referenceAnswer: string) => void;
  onScoringPointsChange?: (scoringPoints: string) => void;
}

export default function SubjectiveQuestionCard({
  question,
  paperId,
  mode,
  answer,
  teacherFeedback,
  aiExplanation,
  onTextAnswerChange,
  onAIGrading,
  onContentChange,
  onReferenceAnswerChange,
  onScoringPointsChange,
}: SubjectiveQuestionCardProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    stem: true,
    answer: true,
    reference: false,
    rules: false,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // 判断是否显示参考答案和评分规则
  // detail/edit 模式显示，answer/preview 模式隐藏
  const showReferenceAndRules = mode === 'detail' || mode === 'edit';

  // 判断是否为编程题类型
  const isCodingType = ['scratch_coding', 'python_coding', 'cpp_coding'].includes(question.type);
  const isTextSubjective = question.type === 'subjective' || question.type === 'coding';

  // 渲染答题区
  const renderAnswerArea = () => {
    if (mode === 'detail') {
      // 详情模式：根据题型渲染对应的 ResultDetail 组件
      if (question.type === 'scratch_coding') {
        return (
          <ScratchResultDetail
            question={question}
            paperId={paperId || ''}
            answer={answer || ''}
            teacherFeedback={teacherFeedback}
          />
        );
      } else if (question.type === 'python_coding') {
        return (
          <PythonCodingResultDetail
            question={question}
            paperId={paperId || ''}
            answer={answer || ''}
            teacherFeedback={teacherFeedback}
          />
        );
      } else if (question.type === 'cpp_coding') {
        return (
          <CppCodingResultDetail
            question={question}
            paperId={paperId || ''}
            answer={answer || ''}
            teacherFeedback={teacherFeedback}
          />
        );
      } else {
        // 文字主观题
        return (
          <SubjectiveTextResultDetail
            question={question}
            answer={answer || ''}
            teacherFeedback={teacherFeedback}
            aiExplanation={aiExplanation}
          />
        );
      }
    } else if (mode === 'answer' || mode === 'preview') {
      // 答题/预览模式：文字主观题显示输入框
      if (isTextSubjective && onTextAnswerChange && onAIGrading) {
        return (
          <SubjectiveTextAnswerArea
            question={question}
            answer={answer || ''}
            isRevealed={mode === 'preview'}
            aiExplanation={aiExplanation}
            onTextAnswerChange={onTextAnswerChange}
            onAIGrading={onAIGrading}
          />
        );
      }
      // 编程题在答题模式下不显示答案区（由专门的编辑器处理）
      return null;
    } else if (mode === 'edit') {
      // 编辑模式：显示提示
      return (
        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
          <p className="text-sm text-yellow-800">
            编辑模式下，请在题目管理页面修改题目内容和答案
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* 1. 题目标题区 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">{question.questionTitle ?? question.name ?? question.questionStem ?? question.title ?? ''}</h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                {question.score}分
              </span>
              {mode === 'edit' && (
                <Edit3 className="w-5 h-5 text-gray-500 cursor-pointer hover:text-blue-600" />
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* 2. 题干区 */}
      {question.content && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <button
            onClick={() => toggleSection('stem')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-indigo-600" />
              <span className="font-medium text-gray-900">题目内容</span>
            </div>
            {expandedSections.stem ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.stem && (
            <div className="p-4">
              {mode === 'edit' && onContentChange ? (
                <QuillEditorWithImageResize
                  value={question.content || ''}
                  onChange={onContentChange}
                  placeholder="请输入题目内容..."
                />
              ) : (
                <QuestionHtmlWithZoom
                  html={question.content}
                />
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* 3. 答题区 */}
      {renderAnswerArea()}

      {/* 4. 参考答案区（仅 detail/edit 模式显示） */}
      {showReferenceAndRules && question.referenceAnswer && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <button
            onClick={() => toggleSection('reference')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-gray-900">参考答案</span>
            </div>
            {expandedSections.reference ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.reference && (
            <div className="p-4">
              {mode === 'edit' && onReferenceAnswerChange ? (
                <QuillEditorWithImageResize
                  value={question.referenceAnswer || ''}
                  onChange={onReferenceAnswerChange}
                  placeholder="请输入参考答案..."
                />
              ) : (
                <QuestionHtmlWithZoom
                  html={question.referenceAnswer || '<p>暂无参考答案说明</p>'}
                />
              )}
            </div>
          )}
        </motion.div>
      )}

      {/* 5. 评分规则区（仅 detail/edit 模式显示） */}
      {showReferenceAndRules && question.scoringPoints && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <button
            onClick={() => toggleSection('rules')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Award className="w-5 h-5 text-orange-600" />
              <span className="font-medium text-gray-900">评分规则</span>
            </div>
            {expandedSections.rules ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.rules && (
            <div className="p-4">
              {mode === 'edit' && onScoringPointsChange ? (
                <QuillEditorWithImageResize
                  value={question.scoringPoints || ''}
                  onChange={onScoringPointsChange}
                  placeholder="请输入评分规则..."
                />
              ) : (
                <QuestionHtmlWithZoom
                  html={question.scoringPoints || '<p>暂无评分规则说明</p>'}
                />
              )}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
